"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.passwordRequireMessage = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const Visibility_1 = __importDefault(require("@mui/icons-material/Visibility"));
const VisibilityOff_1 = __importDefault(require("@mui/icons-material/VisibilityOff"));
const material_1 = require("@mui/material");
const material_2 = require("@mui/material");
const lexico_1 = __importDefault(require("@tradeshift/lexico"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const react_router_1 = require("react-router");
const react_use_1 = require("react-use");
const zod_1 = require("zod");
const strings_1 = require("../../../../../../../constants/strings");
const query_keys_1 = require("../../../../../../../queries/query-keys");
const sessionQuery_1 = require("../../../../../../../queries/sessionQuery");
const userRepository_1 = __importDefault(require("../../../../../../../repositories/userRepository"));
const Root_1 = require("../../../../../../../router/Root");
const routerUtils_1 = require("../../../../../../../router/routerUtils");
const userSelectors_1 = require("../../../../../../../selectors/userSelectors");
const zustandStore_1 = require("../../../../../../../zustandStore");
const pageSpinner_1 = __importDefault(require("../../../../../../molecules/pageSpinner"));
const activationForm_1 = require("../../../../activateForm/activationForm");
const formWrapper_1 = __importDefault(require("../../../formWrapper"));
const useValidation_1 = require("../../../hooks/useValidation");
const formInput_1 = __importDefault(require("../../common/formInput"));
const lexico = new lexico_1.default();
const logger = (0, pino_1.default)();
const isInvitation = !location.pathname.includes('register');
exports.passwordRequireMessage = globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP
    ? 'Please note that passwords must contain a minimum of ten characters, with at least one capital letter, one number and one special character.'
    : 'Please note that passwords must contain a minimum of ten characters, which must include at least one capital letter, one number and one special character.';
const passwordSchema = zod_1.z
    .object({
    password: zod_1.z.string().check(ctx => {
        if (ctx.value.length < 10) {
            ctx.issues.push({
                code: 'custom',
                message: exports.passwordRequireMessage,
                input: ctx.value,
            });
        }
        else if (!activationForm_1.passwordRegex.test(ctx.value)) {
            ctx.issues.push({
                code: 'custom',
                message: exports.passwordRequireMessage,
                input: ctx.value,
            });
        }
    }),
    confirmPassword: zod_1.z.string(),
})
    .refine(data => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords must match',
});
const PasswordSetupForm = () => {
    const initialData = (0, react_router_1.useLoaderData)();
    const { data, isLoading, error } = initialData || {};
    const [formState, setFormState] = (0, react_1.useState)({
        password: '',
        confirmPassword: '',
    });
    const { validateField, clearFieldError, fieldErrors, validateAllSchema } = (0, useValidation_1.useValidation)();
    const activateUserWithTemporaryPassword = (0, sessionQuery_1.useActivateUserQuery)();
    const { userId, activationToken } = (0, react_router_1.useParams)();
    const { steps, setPageLoading, setDialog, closeDialog, setStepsData, setCurrentStep, setNotification } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const navigate = (0, react_router_1.useNavigate)();
    const logout = (0, sessionQuery_1.useSessionLogoutQuery)();
    const [showPassword, setShowPassword] = (0, react_1.useState)(false);
    const [showConfirmPassword, setShowConfirmPassword] = (0, react_1.useState)(false);
    const { navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prev => {
            const updatedState = { ...prev, [name]: value };
            clearFieldError(name);
            return updatedState;
        });
    };
    const handleBlur = (e) => {
        if (e) {
            const { name = '' } = e.target;
            validateField(passwordSchema, name, formState);
        }
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        validateAllSchema(passwordSchema, formState);
        if (!data) {
            logger.error('initial data is missing');
            return;
        }
        setPageLoading(true);
        const options = {
            activationToken: activationToken || '',
            companyName: data?.companyName,
            email: data?.username,
            username: data?.username,
            firstName: steps?.data?.user ? steps?.data?.user.firstName : data?.firstName,
            lastName: steps?.data?.user ? steps?.data?.user.lastName : data?.lastName,
            password: formState.password,
            userId: userId || '',
            temporaryPassword: steps.data?.['tempPassword'] || '',
        };
        activateUserWithTemporaryPassword.mutate(options, {
            onSuccess: async (data) => {
                if (data.phoneSetup || data.mfaOtp || data.mfaSms) {
                    if (options.username) {
                        setStepsData({ email: options.username });
                    }
                    if (data.phoneSetup) {
                        setStepsData({ phoneSetup: data.phoneSetup });
                    }
                    if (data.mfaOtp) {
                        setStepsData({
                            mfaOtp: {
                                status: data.mfaOtp.status,
                                secret: data.mfaOtp.secret || '',
                                qrCode: data.mfaOtp.qrCode,
                            },
                        });
                    }
                    if (data.mfaSms) {
                        setStepsData({ mfaSms: data.mfaSms });
                    }
                    setCurrentStep(steps.currentStep + 1);
                }
                else {
                    if (isInvitation) {
                        setCurrentStep(steps.currentStep + 1);
                        setPageLoading(false);
                    }
                    const user = await userRepository_1.default.getUser();
                    Root_1.queryClient.setQueryData([query_keys_1.userKeys.getUser], user);
                    const isTsAdmin = (0, userSelectors_1.isSupportAdmin)(user?.roles);
                    setPageLoading(false);
                    if (user && !isTsAdmin && !(0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles) && user?.roles.length === 0) {
                        logout.mutate();
                        setFormState({ password: '', confirmPassword: '' });
                        setNotification([
                            {
                                type: strings_1.notificationStrings.ERROR,
                                message: lexico.trc('Error message', 'Your account has been disabled, please contact your company or team administrator.'),
                            },
                        ]);
                    }
                    else if (isTsAdmin) {
                        void navigate('/overview');
                    }
                    else {
                        if ((0, userSelectors_1.isCompanyOrSupportAdmin)(user?.roles || [])) {
                            void navigate('/wallet');
                        }
                        else {
                            void navigate('/wallet/my-cards');
                        }
                    }
                }
            },
            onError: () => {
                setDialog({
                    type: strings_1.notificationStrings.WARNING,
                    message: lexico.trc('Warning message', 'Oh snap! We were not able to create your account.'),
                    open: true,
                    actionText: 'TRY AGAIN',
                    showCancel: false,
                    onAccept: closeDialog,
                });
            },
            onSettled: () => {
                setPageLoading(false);
            },
        });
    };
    (0, react_use_1.useEffectOnce)(() => {
        if (!data) {
            return;
        }
        if (data.activationCodeAttemptsExceeded) {
            navigateToLockedAccountMessage('activation');
            return;
        }
    });
    (0, react_use_1.useEffectOnce)(() => {
        if (!error) {
            return;
        }
        void navigate('/signin/message', {
            state: {
                heading: lexico.tr('Sorry, we are having technical difficulties. Please try again later.'),
                message: lexico.trc('Error message', 'The link to activate your account is invalid or has expired. Please check with the person who invited you to get another invitation.'),
            },
        });
    });
    return ((0, jsx_runtime_1.jsxs)(formWrapper_1.default, { formName: "Setup your password", onSubmit: handleSubmit, submitButton: {
            label: strings_1.sessionStrings.CONTINUE,
            isDisabled: !passwordSchema.safeParse(formState).success,
        }, isFormPending: activateUserWithTemporaryPassword.isPending, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body1", style: {
                    fontStyle: 'normal',
                    lineHeight: '18px',
                    color: '#000000',
                }, children: exports.passwordRequireMessage }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: "Password", name: "password", type: showPassword ? 'text' : 'password', value: formState.password, error: fieldErrors['password'], onChange: handleInputChange, onBlur: handleBlur, endAdornment: formState.password.length > 0 ? ((0, jsx_runtime_1.jsx)(material_2.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(material_2.IconButton, { onClick: () => setShowPassword(prev => !prev), edge: "end", children: showPassword ? (0, jsx_runtime_1.jsx)(Visibility_1.default, { fontSize: "small" }) : (0, jsx_runtime_1.jsx)(VisibilityOff_1.default, { fontSize: "small" }) }) })) : null }), (0, jsx_runtime_1.jsx)(formInput_1.default, { label: strings_1.sessionStrings.CONFIRM_PASSWORD, name: "confirmPassword", type: showConfirmPassword ? 'text' : 'password', value: formState.confirmPassword, error: fieldErrors['confirmPassword'], onChange: handleInputChange, onBlur: handleBlur, endAdornment: formState.confirmPassword.length > 0 ? ((0, jsx_runtime_1.jsx)(material_2.InputAdornment, { position: "end", children: (0, jsx_runtime_1.jsx)(material_2.IconButton, { onClick: () => setShowConfirmPassword(prev => !prev), edge: "end", children: showConfirmPassword ? (0, jsx_runtime_1.jsx)(Visibility_1.default, { fontSize: "small" }) : (0, jsx_runtime_1.jsx)(VisibilityOff_1.default, { fontSize: "small" }) }) })) : null }), isLoading ? (0, jsx_runtime_1.jsx)(pageSpinner_1.default, { isLoading: true }) : null] }));
};
exports.default = PasswordSetupForm;
